import {
  AccountData,
  Event,
  ThemeType,
  TwilioPhoneType,
  VerificationState,
} from "@markit/common.types";
import { ThemeProvider } from "@mui/material";
import React, {
  useCallback,
  useEffect,
  useMemo,
  useRef,
  useState,
  useContext,
  CSSProperties,
} from "react";
import { API } from "../../API";
import { themeMui } from "../FullEvent/FullEventInputForm";
import { MixpanelContext } from "../../context/AnalyticsService";
import {
  getUserData,
  getPhoneNumberSnap,
  getPhoneNumberData,
} from "../../utils/FirebaseUtils";
import { Colors } from "../../utils/colors";
import { isEventExternalLink, isEventTicketsPaid } from "@markit/common.utils";
import { useTheme } from "../../hooks/useTheme";
import {
  checkIfPhoneNumberIsUnsubscribed,
  checkIfUserIsSubscribed,
  checkIfUserIsUnsubscribedMarkit,
} from "../../utils/userUtils";
import RectangleButton from "../Buttons/RectangleButton";
import VerificationCodeTextField from "../TextFields/VerificationCodeTextField";
import { firebaseAuth } from "../../firebase";
import {
  RecaptchaVerifier,
  signInWithPhoneNumber,
  PhoneAuthProvider,
  signInWithCredential,
} from "firebase/auth";
import { useLogin } from "../../hooks/useLogin";
import OptedOutBanner from "./OptedOutBanner";
import CompliancePhoneOptIn from "../Compliance/CompliancePhoneOptIn";
import { useSelector } from "react-redux";
import { getAccountState } from "../../redux/slices/accountSlice";
import { isValidUSPhoneNumber } from "../../utils/FormatPhoneNumber";
import InternationalNumberNotice from "./InternationalNumberNotice";
import { useLocation } from "react-router-dom";

export enum VerificationCodeOrigin {
  EVENT = "Event",
  PROFILE = "Profile",
  SIGN_IN = "Sign In",
}

type VerificationCodeButtonProp = {
  host?: AccountData;
  event?: Event;
  origin: VerificationCodeOrigin;
  buttonText: string;
  tempFullName?: string;
  tempPhoneNumber: string;
  setNewUid?: (newUid: string) => void;
  verificationState: VerificationState;
  setVerificationState: (verificationState: VerificationState) => void;
  optedOut: boolean;
  setOptedOut: (optedOut: boolean) => void;
  optedOutCreator?: boolean;
  setOptedOutCreator?: (optedOutCreator: boolean) => void;
  showCodeInputError: boolean;
  setShowCodeInputError: (showCodeInputError: boolean) => void;
  showError: () => boolean;
  afterVerifiedAction?: () => Promise<void>;
  callbackOnVerify?: () => void;
  processing?: boolean;
  setAlreadyFollowing?: (alreadyFollowing: boolean) => void;
  alreadyFollowingUid?: string;
  disableVerifyNumber?: boolean;
  buttonContainerStyles?: CSSProperties;
  themeType?: ThemeType;
};

// IMPORTANT (jonathan): Anywhere new this component is used, need to add RecaptchaVerifierWrapper top level for the recatcha verifier
// Put it in a place where the verifier won't show up on the screen, so ideally as high level as possible. But also needs to be something
// that will remount before every attempt to verify because you can't reuse the same verifier div more than once
// Currently exists in:
// 1) CheckoutContainer
// 2) SubscribeFlowInitial
// 3) FullForm
// 4) LoginWelcome
const VerificationCodeButton = (props: VerificationCodeButtonProp) => {
  const {
    host,
    event,
    origin,
    buttonText,
    tempFullName,
    tempPhoneNumber,
    setNewUid,
    verificationState,
    setVerificationState,
    optedOut,
    setOptedOut,
    optedOutCreator,
    setOptedOutCreator,
    showCodeInputError,
    setShowCodeInputError,
    showError,
    afterVerifiedAction,
    callbackOnVerify,
    processing,
    setAlreadyFollowing,
    alreadyFollowingUid,
    disableVerifyNumber,
    buttonContainerStyles,
    themeType,
  } = props;
  const mixpanel = useContext(MixpanelContext);
  const { theme } = useTheme(themeType);
  const { loginWithCredential } = useLogin();
  const email = useSelector(getAccountState).account.accountData.email;
  const pathname = useLocation().pathname;
  const [loading, setLoading] = useState(false);
  const [verificationCode, setVerificationCode] = useState<string[]>(
    Array(6).fill("")
  );
  const [sendingVerificationCode, setSendingVerificationCode] = useState(false);
  const [defaultToMarkitOTP, setDefaultToMarkitOTP] = useState(false);
  const [verificationId, setVerificationId] = useState("");
  const [optInChecked, setOptInChecked] = useState(true);

  const ref0 = useRef<HTMLElement>();
  const ref1 = useRef<HTMLElement>();
  const ref2 = useRef<HTMLElement>();
  const ref3 = useRef<HTMLElement>();
  const ref4 = useRef<HTMLElement>();
  const ref5 = useRef<HTMLElement>();
  const refs = useMemo(() => [ref0, ref1, ref2, ref3, ref4, ref5], []);

  const signIn = useMemo(
    () => origin === VerificationCodeOrigin.SIGN_IN,
    [origin]
  );

  const isMembershipFlow = useMemo(
    () => pathname.includes("memberships"),
    [pathname]
  );

  const disableVerifyNumberButton = useMemo(
    () => sendingVerificationCode || disableVerifyNumber || !optInChecked,
    [disableVerifyNumber, optInChecked, sendingVerificationCode]
  );

  const changeStateToVerifying = useCallback(async () => {
    const phoneNumberSnap = await getPhoneNumberSnap(tempPhoneNumber);
    // mixpanel track if for signing up, not for login verification code
    if (signIn && !phoneNumberSnap.exists()) {
      mixpanel.track("Webapp: Start User Sign Up", {
        customer_phone: tempPhoneNumber,
      });
    }
    setVerificationState(VerificationState.VERIFYING);
    setSendingVerificationCode(false);
  }, [mixpanel, setVerificationState, signIn, tempPhoneNumber]);

  const sendMarkitOTP = useCallback(async () => {
    API.text
      .sendVerificationCode({ toPhoneNumber: tempPhoneNumber })
      .then(() => {
        setVerificationState(VerificationState.VERIFYING);
        setSendingVerificationCode(false);
      })
      .catch((err) => {
        alert("Error: " + err.message);
        console.log(err.message);
        setSendingVerificationCode(false);
      });
  }, [setVerificationState, tempPhoneNumber]);

  const sendVerificationCode = useCallback(async () => {
    if (showError()) {
      return;
    }

    let returnEarly = false;
    // Check if phone number is unsubscribed from markit number or creator
    // Even if using firebase OTP, we need them opted in so they can get confirmation texts
    const isUnsubscribedMarkit = await checkIfUserIsUnsubscribedMarkit(
      tempPhoneNumber
    );
    if (isUnsubscribedMarkit) {
      setOptedOut(isUnsubscribedMarkit);
      setVerificationState(VerificationState.VERIFYING);
      setSendingVerificationCode(false);
      returnEarly = true;
    }

    if (host && setOptedOutCreator) {
      const isUnsubscribed = await checkIfPhoneNumberIsUnsubscribed(
        tempPhoneNumber,
        host.uid
      );
      if (isUnsubscribed) {
        setOptedOutCreator(isUnsubscribed);
        setVerificationState(VerificationState.VERIFYING);
        returnEarly = true;
      }
    }
    if (returnEarly) {
      return;
    }

    setSendingVerificationCode(true);

    // If the phone number is not a US number, we will skip the verification code logic
    if (!isValidUSPhoneNumber(tempPhoneNumber)) {
      await changeStateToVerifying();
      return;
    }

    // this will only happen when we're resending the verification code
    if (defaultToMarkitOTP) {
      sendMarkitOTP();
      return;
    }

    // initialize recaptcha verifier
    window.recaptchaVerifier = new RecaptchaVerifier(
      "sign-in-button",
      {
        size: "invisible",
        callback: (response: any) => {
          console.log("Successfully sent OTP");
        },
      },
      firebaseAuth
    );
    await window.recaptchaVerifier.verify();

    // mixpanel logs and checks
    if (mixpanel) {
      if (signIn) {
        mixpanel.track("Webapp: Send Verification Code For Login", {
          customer_phone: tempPhoneNumber,
        });
      } else {
        if (event) {
          if (event.locationBasedRSVP) {
            alert("This event requires you to download the app");
            setSendingVerificationCode(false);
            return;
          }
          mixpanel.track("Webapp: Send Verification Code", {
            event_id: event.id,
            event_type: isEventTicketsPaid(event) ? "paid" : "free",
            customer_phone: tempPhoneNumber,
            link_type: event.eventType,
          });
        } else {
          mixpanel.track("Webapp: Send Verification Code For Follow", {
            customer_phone: tempPhoneNumber,
          });
        }
      }
    }

    // First attempt to send OTP through Firebase
    signInWithPhoneNumber(
      firebaseAuth,
      tempPhoneNumber,
      window.recaptchaVerifier
    )
      .then(async (confirmationResult) => {
        await changeStateToVerifying();
        setVerificationId(confirmationResult.verificationId);
      })
      .catch(async (err) => {
        // if sign in, this should fail and show too many requests
        if (signIn) {
          console.log(err.message);
          alert("Too many attempts, please try again in one hour.");
          setSendingVerificationCode(false);
          window.location.reload();
          return;
        }

        // if Firebase OTP fails, try sending OTP through Markit
        setDefaultToMarkitOTP(true);

        sendMarkitOTP();
      });
  }, [
    showError,
    tempPhoneNumber,
    host,
    setOptedOutCreator,
    defaultToMarkitOTP,
    mixpanel,
    setOptedOut,
    setVerificationState,
    changeStateToVerifying,
    sendMarkitOTP,
    signIn,
    event,
  ]);

  const verifyPhoneNumber = useCallback(async () => {
    setLoading(true);
    let error = false;
    let uid = "";

    // Skip verification if phone number is not a US number
    if (!isValidUSPhoneNumber(tempPhoneNumber)) {
      const phoneNumberData = await getPhoneNumberData(tempPhoneNumber);
      if (phoneNumberData) {
        uid = phoneNumberData.userId;
      }
    } else {
      // Standard verification flow
      if (verificationCode.filter((el) => el !== undefined).length !== 6) {
        setShowCodeInputError(true);
        setLoading(false);
        alert("Verification Code is Incorrect");
        return;
      }

      const finalCode = verificationCode.join("");
      // verify with Markit OTP
      if (defaultToMarkitOTP) {
        const response = await API.text
          .verifyCode({
            phoneNumber: tempPhoneNumber,
            code: finalCode,
          })
          .catch((err: any) => {
            setShowCodeInputError(true);
            setLoading(false);
            alert("Verification Code is Incorrect");
            console.log(err.message);
            error = true;
          });
        uid = response?.uid ?? "";
      } else {
        // Verify with Firebase OTP
        if (verificationId) {
          const credential = PhoneAuthProvider.credential(
            verificationId,
            finalCode
          );
          await API.user
            .createPhoneAuthUser({ phoneNumber: tempPhoneNumber })
            .then(async () => {
              await signInWithCredential(firebaseAuth, credential)
                .then(async (result) => {
                  uid = result.user.uid;
                  await loginWithCredential(result, signIn, tempFullName);
                })
                .catch((err) => {
                  alert("Verification Code is Incorrect");
                  console.error("Verification Code Error: " + err.message);
                  setShowCodeInputError(true);
                  setLoading(false);
                  error = true;
                });
            });
        } else {
          setShowCodeInputError(true);
          setLoading(false);
          alert("Error: No Verification Code");
          return;
        }
      }
    }

    // Can't return out of catch errors, so made a error flag that we set to true if an error occurred in one of the catches
    if (error) {
      return;
    }
    if (uid !== undefined && setNewUid) {
      setNewUid(uid);
      mixpanel.identify(uid);
    }

    // for profile page follow flow: check if user is already following user
    if (uid && setAlreadyFollowing) {
      const userData = await getUserData(uid);
      if (userData && alreadyFollowingUid) {
        const checkFollowing = await checkIfUserIsSubscribed(
          uid,
          alreadyFollowingUid
        );
        setAlreadyFollowing(checkFollowing);
      }
    }

    if (mixpanel) {
      if (event) {
        mixpanel.track("Webapp: Successful Verify", {
          event_id: event.id,
          event_type: isEventTicketsPaid(event) ? "paid" : "free",
          customer_phone: tempPhoneNumber,
          link_type: event.eventType,
        });
      } else if (!signIn) {
        mixpanel.track("Webapp: Successful Verify For Follow", {
          customer_phone: tempPhoneNumber,
        });
      }
      mixpanel.track("Webapp: Agreed to Terms", {
        distinct_id: uid,
        type: event ? "RSVP" : signIn ? "Onboarding" : "Subscribe",
        event_id: event ? event.id : "",
        customer_phone: tempPhoneNumber,
        creator_uid: host ? host.uid : "",
      });
    }

    setVerificationState(VerificationState.VERIFIED);

    // If defined, apply extra callback on verify press
    if (callbackOnVerify) {
      callbackOnVerify();
    }
    setLoading(false);
  }, [
    alreadyFollowingUid,
    callbackOnVerify,
    defaultToMarkitOTP,
    event,
    host,
    loginWithCredential,
    mixpanel,
    setAlreadyFollowing,
    setNewUid,
    setShowCodeInputError,
    setVerificationState,
    signIn,
    tempFullName,
    tempPhoneNumber,
    verificationCode,
    verificationId,
  ]);

  const reload = useCallback(async () => {
    window.location.reload();
  }, []);

  useEffect(() => {
    if (afterVerifiedAction) {
      if (verificationState === VerificationState.VERIFIED) {
        afterVerifiedAction();
      }
    }
  }, [afterVerifiedAction, verificationCode, verificationState]);

  const renderComplianceOptIn = useMemo(
    () =>
      !host || (host && !host.showTextComplianceCheckbox) ? (
        <CompliancePhoneOptIn
          isChecked={optInChecked}
          setIsChecked={setOptInChecked}
          hostShowComplianceCheckbox={host?.showTextComplianceCheckbox}
          hostPhoneType={host?.customer.phoneType || TwilioPhoneType.TOLL_FREE}
          hostName={host ? host.fullName : undefined}
          themeType={themeType}
        />
      ) : null,
    [host, optInChecked, setOptInChecked, themeType]
  );

  const renderAgreeButton = useMemo(
    () => (
      <div>
        <RectangleButton
          buttonLabel={
            <span style={{ color: buttonContainerStyles?.color }}>I Agree</span>
          }
          onPress={verifyPhoneNumber}
          theme={theme}
          altPaddingVert={12}
          disabled={loading}
          loading={loading}
          containerStyles={buttonContainerStyles}
          altColor={buttonContainerStyles?.backgroundColor || Colors.BLACK}
        />
      </div>
    ),
    [buttonContainerStyles, loading, theme, verifyPhoneNumber]
  );

  if (verificationState === VerificationState.UNVERIFIED) {
    return (
      <ThemeProvider theme={themeMui}>
        {/** For when an acount is being converted to subaccount and phone number gets moved */}
        {/* {event?.createdBy !== "WSXfSxoVsUWf4KWHDBVCB1UEi3t2" &&
        hostFullName !== "LA ROB" ? ( */}
        <RectangleButton
          buttonLabel={
            <span style={{ color: buttonContainerStyles?.color }}>
              {buttonText}
            </span>
          }
          onPress={sendVerificationCode}
          theme={theme}
          altPaddingVert={12}
          disabled={disableVerifyNumberButton}
          loading={sendingVerificationCode}
          containerStyles={{
            ...buttonContainerStyles,
            opacity: disableVerifyNumberButton ? 0.6 : 1,
          }}
          altColor={buttonContainerStyles?.backgroundColor || Colors.BLACK}
        />
        {/* ) : null} */}
        {/* {!signIn ? (
          <p
            style={{
              fontSize: 12,
              fontWeight: "500",
              textAlign: "center",
              marginInline: 20,
              color: "#929292",
            }}
          >
            <span
              style={{ color: Colors.RED1, fontWeight: "600", fontSize: 14 }}
            >
              T-Mobile users!
            </span>{" "}
            Due to an outage, sign in at the top right of the screen using your
            phone number then return to{" "}
            {event ? "the event page to get tickets" : "this page to subscribe"}
          </p>
        ) : null} */}
      </ThemeProvider>
    );
  } else if (verificationState === VerificationState.VERIFYING) {
    return (
      <div className="ColumnNormal">
        {optedOut || optedOutCreator ? (
          <OptedOutBanner
            host={host}
            optedOut={optedOut}
            optedOutCreator={optedOutCreator}
          />
        ) : isValidUSPhoneNumber(tempPhoneNumber) ? (
          <div className="ColumnNormal" style={{ gap: 20 }}>
            <div className="ColumnNormal" style={{ gap: 7 }}>
              <span
                className="LoginHeaderText"
                style={{ ...theme.PrimaryText }}
              >
                Enter Code
              </span>
              <span className="LoginHeaderTextDescription">
                We sent a code to {tempPhoneNumber}. Enter below to continue.
              </span>
            </div>
            <div className="textFieldDiv" style={{ alignSelf: "center" }}>
              {Array.from(Array(6).keys()).map((elem, index) => {
                return (
                  <div key={index}>
                    <VerificationCodeTextField
                      index={index}
                      elem={elem}
                      showCodeInputError={showCodeInputError}
                      setShowCodeInputError={setShowCodeInputError}
                      verificationCode={verificationCode}
                      setVerificationCode={setVerificationCode}
                      refs={refs}
                      themeType={themeType}
                    />
                  </div>
                );
              })}
            </div>
            <div className="ColumnNormal" style={{ gap: 7 }}>
              <div>
                <span style={{ color: Colors.GRAY2, fontSize: 12 }}>
                  {"Didn't receive a code? "}
                </span>
                <span
                  style={{
                    ...theme.LinkColor,
                    fontSize: 12,
                    cursor: "pointer",
                  }}
                  onClick={() => {
                    if (!defaultToMarkitOTP) {
                      reload();
                    } else {
                      sendVerificationCode();
                      alert("Resent!");
                    }
                  }}
                >
                  Resend code
                </span>
              </div>
              {renderComplianceOptIn}
            </div>
            {renderAgreeButton}
          </div>
        ) : (
          <div className="ColumnNormal" style={{ gap: 20 }}>
            <InternationalNumberNotice signIn={signIn} />
            {signIn ? null : (
              <>
                {renderComplianceOptIn}
                {renderAgreeButton}
              </>
            )}
          </div>
        )}
      </div>
    );
  } else {
    // once you're verified, this is what else can show
    if (optedOut || optedOutCreator) {
      return (
        <OptedOutBanner
          host={host}
          optedOut={optedOut}
          optedOutCreator={optedOutCreator}
        />
      );
    } else if (
      (origin === VerificationCodeOrigin.PROFILE ||
        (event && isEventExternalLink(event.eventType))) &&
      !email &&
      callbackOnVerify
    ) {
      return (
        <RectangleButton
          buttonLabel={
            <span style={{ color: buttonContainerStyles?.color }}>
              {origin === VerificationCodeOrigin.PROFILE && !isMembershipFlow
                ? "Subscribe"
                : "Continue"}
            </span>
          }
          onPress={callbackOnVerify}
          theme={origin === VerificationCodeOrigin.PROFILE ? undefined : theme}
          altPaddingVert={12}
          disabled={processing}
          loading={processing}
          altColor={buttonContainerStyles?.backgroundColor || Colors.BLACK}
          altTextColor={buttonContainerStyles?.color || Colors.WHITE}
        />
      );
    } else {
      return <></>;
    }
  }
};

export default VerificationCodeButton;
